import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  Timestamp, 
  doc, 
  updateDoc 
} from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { AdminService } from './adminService';
import { secureEmailService } from './secureEmailService';
import { Automation } from './automationService';

export interface PlatformCampaign {
  id?: string;
  name: string;
  type: 'welcome' | 'promotional' | 'announcement';
  status: 'active' | 'paused' | 'draft';
  subject: string;
  content: string;
  senderEmail: string;
  recipientSegment: 'all_users' | 'new_users' | 'active_users';
  schedule?: {
    type: 'immediate' | 'weekly' | 'daily';
    dayOfWeek?: number; // 0-6
    time?: string; // HH:mm
  };
  stats: {
    sent: number;
    opened: number;
    clicked: number;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

class PlatformEmailService {
  private readonly SENDER_EMAIL = 'hello@mail.scubamail.io';

  // ── In-memory TTL cache for getSystemAutomation() ────────────────────────
  // Each call hits Firestore on every email send (verification, password reset,
  // plan activation, etc.). With caching, a cold read happens once per key per
  // 5 minutes instead of once per email sent.
  private automationCache = new Map<string, { data: import('./automationService').Automation | null; expires: number }>();
  private readonly CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  // Get all platform and system communications
  async getPlatformCommunications() {
    const q = query(
      collection(db, 'automations'),
      where('category', 'in', ['platform', 'system'])
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }

  // Get a specific active system automation (cached — 5 min TTL)
  async getSystemAutomation(key: string): Promise<import('./automationService').Automation | null> {
    const cached = this.automationCache.get(key);
    if (cached && Date.now() < cached.expires) {
      return cached.data;
    }

    const q = query(
      collection(db, 'automations'),
      where('systemKey', '==', key),
      where('category', 'in', ['system', 'platform']),
      where('status', '==', 'active')
    );
    const snapshot = await getDocs(q);
    const data = snapshot.empty
      ? null
      : ({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as import('./automationService').Automation);

    this.automationCache.set(key, { data, expires: Date.now() + this.CACHE_TTL_MS });
    return data;
  }

  /** Invalidate a single cached automation (call after saving edits in the admin UI). */
  invalidateCachedAutomation(key: string): void {
    this.automationCache.delete(key);
  }

  /** Invalidate all cached automations (call after ensureDefaults or bulk updates). */
  invalidateAllCachedAutomations(): void {
    this.automationCache.clear();
  }

  // Initialize default platform and system communications if they don't exist
  async ensureDefaults() {
    const allComms = await this.getPlatformCommunications();
    
    // 1. Welcome Series (Platform)
    if (!allComms.some((c: any) => c.name.toLowerCase().includes('welcome') && c.category === 'platform')) {
      await addDoc(collection(db, 'automations'), {
        name: 'Welcome to Scubamail',
        category: 'platform',
        type: 'welcome',
        status: 'active',
        senderEmail: this.SENDER_EMAIL,
        description: 'Automatic welcome email sent to all new signups.',
        steps: [
          {
            id: 'welcome_1',
            type: 'trigger',
            title: 'New User Signup',
            description: 'Triggers when a new account is created',
            config: { event: 'user_signup' }
          },
          {
            id: 'welcome_2',
            type: 'action',
            title: 'Send Welcome Email',
            description: 'Send core brand welcome message',
            config: { 
              subject: 'Welcome to Scubamail! 🚀',
              sender: this.SENDER_EMAIL,
              template: 'welcome_v1'
            }
          }
        ],
        updatedAt: Timestamp.now(),
        userId: 'system'
      });
    }

    // 2. User Verification (System)
    if (!allComms.some((c: any) => c.systemKey === 'user_verification')) {
      await addDoc(collection(db, 'automations'), {
        name: 'System: User Verification',
        category: 'system',
        systemKey: 'user_verification',
        status: 'active',
        senderEmail: this.SENDER_EMAIL,
        description: 'Sent when a user needs to verify their email (contains {{passcode}}).',
        steps: [
          {
            id: 'verify_1',
            type: 'action',
            title: 'Send Verification Code',
            description: 'Email containing the 6-digit passcode',
            config: { 
              subject: 'Verify Your Email',
              fromName: 'Scubamail',
              fromEmail: this.SENDER_EMAIL,
              htmlBody: `
                <div style="font-family: sans-serif; max-width: 600px; margin: auto; padding: 20px;">
                  <h2>Verify Your Email</h2>
                  <p>Hi {{first_name}},</p>
                  <p>To complete your registration, please enter the following verification code in the app:</p>
                  <div style="font-size: 32px; font-weight: bold; letter-spacing: 5px; margin: 20px 0;">{{passcode}}</div>
                  <p>This code will expire in 10 minutes. If you didn't request this, please ignore this email.</p>
                </div>
              `.trim()
            }
          }
        ],
        updatedAt: Timestamp.now(),
        userId: 'system'
      });
    }

    // 3. Password Reset (System)
    if (!allComms.some((c: any) => c.systemKey === 'password_reset')) {
      await addDoc(collection(db, 'automations'), {
        name: 'System: Password Reset',
        category: 'system',
        systemKey: 'password_reset',
        status: 'active',
        senderEmail: this.SENDER_EMAIL,
        description: 'Sent when a user requests a password reset (contains {{passcode}}).',
        steps: [
          {
            id: 'reset_1',
            type: 'action',
            title: 'Send Reset Code',
            description: 'Email containing the password reset passcode',
            config: { 
              subject: 'Reset Your Password',
              fromName: 'Scubamail',
              fromEmail: this.SENDER_EMAIL,
              htmlBody: `
                <div style="font-family: sans-serif; max-width: 600px; margin: auto; padding: 20px;">
                  <h2>Password Reset Request</h2>
                  <p>Hi {{first_name}},</p>
                  <p>We received a request to reset your password. Use the code below to proceed:</p>
                  <div style="font-size: 32px; font-weight: bold; letter-spacing: 5px; margin: 20px 0;">{{passcode}}</div>
                  <p>This code is valid for 1 hour. If you didn't request this, your account is safe.</p>
                </div>
              `.trim()
            }
          }
        ],
        updatedAt: Timestamp.now(),
        userId: 'system'
      });
    }

    // 4. Founder Welcome (System)
    if (!allComms.some((c: any) => c.systemKey === 'founder_welcome')) {
      await addDoc(collection(db, 'automations'), {
        name: 'System: Founder Welcome',
        category: 'system',
        systemKey: 'founder_welcome',
        status: 'active',
        senderEmail: 'abhilekh@scubamail.io',
        description: 'The personal note from the founder sent 5 minutes after signup.',
        steps: [
          {
            id: 'founder_1',
            type: 'action',
            title: 'Send Personal Note',
            description: 'Handwritten-style welcome from Abhilekh',
            config: { 
              subject: 'Welcome to Scubamail - A note from the founder',
              fromName: 'Abhilekh from Scubamail',
              fromEmail: 'abhilekh@scubamail.io',
              htmlBody: `
<body style="margin:0; padding:0; background-color:#f7f7f7; font-family: Georgia, 'Poppins', serif;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr>
      <td align="center" style="padding: 60px 20px;">
        <table width="100%" max-width="520px" cellpadding="0" cellspacing="0" border="0" style="background:#ffffff; border-radius:12px; padding:50px 35px;">
          <tr><td align="center" style="padding-bottom: 30px;"><h2 style="margin:0; font-weight:500; letter-spacing:1px; color:#111;">Scubamail</h2></td></tr>
          <tr><td style="padding-bottom: 25px;"><p style="margin:0; font-size:16px; color:#333; line-height:1.7;">Hi {{first_name}},</p></td></tr>
          <tr><td style="padding-bottom: 25px;"><p style="margin:0; font-size:16px; color:#333; line-height:1.7;">I'm Abhilekh, the founder of Scubamail. I wanted to personally thank you for joining our community.</p></td></tr>
          <tr><td style="padding-bottom: 30px;"><p style="margin:0; font-size:16px; color:#333; line-height:1.7;">We built Scubamail to make email marketing simple, powerful, and actually enjoyable. I'd love to hear what you're planning to build with it.</p></td></tr>
          <tr><td align="center" style="padding-bottom: 20px;"><p style="margin:0; font-size:15px; color:#111; line-height:1.6;">— Abhilekh<br><span style="color:#777; font-size:13px;">Founder, Scubamail</span></p></td></tr>
        </table>
      </td>
    </tr>
  </table>
</body>
              `.trim()
            }
          }
        ],
        updatedAt: Timestamp.now(),
        userId: 'system'
      });
    }

    // 5. Admin Signup Notification (System)
    if (!allComms.some((c: any) => c.systemKey === 'admin_signup_notification')) {
      await addDoc(collection(db, 'automations'), {
        name: 'System: Admin Signup Notification',
        category: 'system',
        systemKey: 'admin_signup_notification',
        status: 'active',
        senderEmail: this.SENDER_EMAIL,
        description: 'Notification sent to admins when a new user signs up.',
        steps: [
          {
            id: 'admin_notify_1',
            type: 'action',
            title: 'Notify Admin',
            description: 'Detailed signup info for internal use',
            config: { 
              subject: '🚀 New User Signup: {{email}}',
              fromName: 'Scubamail System',
              fromEmail: this.SENDER_EMAIL,
              htmlBody: `
                <div style="font-family: sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px;">
                  <h2 style="color: #6366f1;">New User Signup</h2>
                  <p>A new user has registered:</p>
                  <ul>
                    <li><strong>Email:</strong> {{email}}</li>
                    <li><strong>UID:</strong> {{uid}}</li>
                    <li><strong>Provider:</strong> {{provider}}</li>
                    <li><strong>City:</strong> {{city}}</li>
                    <li><strong>Country:</strong> {{country}}</li>
                  </ul>
                  <p>View more details in the admin dashboard.</p>
                </div>
              `.trim()
            }
          }
        ],
        updatedAt: Timestamp.now(),
        userId: 'system'
      });
    }

    // 6. Sender Email Verification (System)
    if (!allComms.some((c: any) => c.systemKey === 'sender_verification')) {
      await addDoc(collection(db, 'automations'), {
        name: 'System: Sender Email Verification',
        category: 'system',
        systemKey: 'sender_verification',
        status: 'active',
        senderEmail: this.SENDER_EMAIL,
        description: 'Sent when a user adds a sender email address and must verify they own it (contains {{passcode}}).',
        steps: [
          {
            id: 'sender_verify_1',
            type: 'action',
            title: 'Send Sender Verification Code',
            description: 'Email containing the 6-digit passcode for sender address verification',
            config: {
              subject: 'Verify Sender Email Address - Scubamail',
              fromName: 'Scubamail',
              fromEmail: this.SENDER_EMAIL,
              htmlBody: `
<div style="font-family: sans-serif; max-width: 600px; margin: auto; padding: 20px;">
  <h2>Verify Your Sender Email</h2>
  <p>Hi {{first_name}},</p>
  <p>To verify <strong>{{sender_email}}</strong> as a sending address for your campaigns, enter the code below:</p>
  <div style="font-size: 32px; font-weight: bold; letter-spacing: 5px; margin: 20px 0; text-align: center;">{{passcode}}</div>
  <p style="color: #666; font-size: 14px;">This code expires in 15 minutes. If you didn't request this, you can safely ignore this email.</p>
</div>
              `.trim()
            }
          }
        ],
        updatedAt: Timestamp.now(),
        userId: 'system'
      });
    }

    // 7. Plan Activation / Billing Confirmation (System)
    if (!allComms.some((c: any) => c.systemKey === 'plan_activation')) {
      await addDoc(collection(db, 'automations'), {
        name: 'System: Plan Activation',
        category: 'system',
        systemKey: 'plan_activation',
        status: 'active',
        senderEmail: 'billing@scubamail.io',
        description: 'Sent to a user after they successfully activate or upgrade a paid plan.',
        steps: [
          {
            id: 'plan_activation_1',
            type: 'action',
            title: 'Send Plan Confirmation',
            description: 'Billing confirmation with plan details',
            config: {
              subject: 'Your {{plan_name}} plan is now active',
              fromName: 'Scubamail Billing',
              fromEmail: 'billing@scubamail.io',
              htmlBody: `
<div style="font-family: sans-serif; max-width: 600px; margin: auto; padding: 20px;">
  <h2>Your {{plan_name}} Plan is Active 🎉</h2>
  <p>Hi {{first_name}},</p>
  <p>Your subscription has been updated successfully. Here are your plan details:</p>
  <table style="width:100%; border-collapse:collapse; margin: 16px 0;">
    <tr><td style="padding:8px; border-bottom:1px solid #eee;"><strong>Plan</strong></td><td style="padding:8px; border-bottom:1px solid #eee;">{{plan_name}}</td></tr>
    <tr><td style="padding:8px; border-bottom:1px solid #eee;"><strong>Billing</strong></td><td style="padding:8px; border-bottom:1px solid #eee;">{{billing_period}}</td></tr>
    <tr><td style="padding:8px; border-bottom:1px solid #eee;"><strong>Amount</strong></td><td style="padding:8px; border-bottom:1px solid #eee;">{{amount}} {{currency}}</td></tr>
    <tr><td style="padding:8px; border-bottom:1px solid #eee;"><strong>Start Date</strong></td><td style="padding:8px; border-bottom:1px solid #eee;">{{start_date}}</td></tr>
    <tr><td style="padding:8px; border-bottom:1px solid #eee;"><strong>Next Renewal</strong></td><td style="padding:8px; border-bottom:1px solid #eee;">{{end_date}}</td></tr>
  </table>
  <p>You can manage your billing anytime from your account settings.</p>
  <p style="color: #666; font-size: 14px;">If you have any questions, reply to this email or contact support.</p>
</div>
              `.trim()
            }
          }
        ],
        updatedAt: Timestamp.now(),
        userId: 'system'
      });
    }

    // 8. Double Opt-in Subscription Confirmation (System)
    if (!allComms.some((c: any) => c.systemKey === 'double_optin')) {
      await addDoc(collection(db, 'automations'), {
        name: 'System: Double Opt-in Confirmation',
        category: 'system',
        systemKey: 'double_optin',
        status: 'active',
        senderEmail: this.SENDER_EMAIL,
        description: 'Sent to a contact who submits a signup form with double opt-in enabled. Contains {{confirm_link}}.',
        steps: [
          {
            id: 'double_optin_1',
            type: 'action',
            title: 'Send Confirmation Request',
            description: 'Ask the contact to confirm their subscription',
            config: {
              subject: 'Please confirm your subscription',
              fromName: 'Scubamail',
              fromEmail: this.SENDER_EMAIL,
              htmlBody: `
<div style="font-family: sans-serif; max-width: 600px; margin: auto; padding: 20px;">
  <h2>Confirm Your Subscription</h2>
  <p>Hi there,</p>
  <p>Thanks for signing up! Please click the button below to confirm your email address and complete your subscription.</p>
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{confirm_link}}" style="background:#111; color:#fff; padding:14px 28px; border-radius:6px; text-decoration:none; font-weight:600; font-size:15px;">Confirm Subscription</a>
  </div>
  <p style="color: #666; font-size: 13px;">Or copy and paste this link into your browser:<br>{{confirm_link}}</p>
  <p style="color: #666; font-size: 13px;">If you didn't sign up, you can safely ignore this email.</p>
</div>
              `.trim()
            }
          }
        ],
        updatedAt: Timestamp.now(),
        userId: 'system'
      });
    }

    await this.syncFounderWelcomeTemplate();
  }

  /** Push bundled founder welcome HTML to Firestore (admin-only callable). */
  async syncFounderWelcomeTemplate(): Promise<void> {
    try {
      const fn = httpsCallable(functions, 'syncFounderWelcomeTemplate');
      await fn({});
    } catch (error) {
      console.warn('syncFounderWelcomeTemplate failed (deploy functions if missing):', error);
    }
  }

  // Target all users for a broadcast
  async sendBroadcastToAllUsers(subject: string, htmlContent: string) {
    try {
      const allUsers = await AdminService.getAllUsers();
      console.log(`📡 Preparing broadcast to ${allUsers.length} users...`);

      const results = [];
      for (const user of allUsers) {
        if (user.email) {
          try {
            const res = await secureEmailService.sendEmail({
              from: { email: this.SENDER_EMAIL, name: 'Scubamail' },
              to: [{ email: user.email, name: user.displayName || user.firstName }],
              subject,
              htmlBody: htmlContent,
              tags: { Source: 'system', Type: 'platform_broadcast' },
            });
            results.push(res);
          } catch (e) {
            console.error(`Failed to send to ${user.email}:`, e);
          }
        }
      }
      return results;
    } catch (error) {
      console.error('Broadcast failed:', error);
      throw error;
    }
  }

  // Save or update a platform automation
  async savePlatformAutomation(data: Partial<PlatformCampaign>) {
    const q = query(
      collection(db, 'automations'),
      where('name', '==', data.name),
      where('category', 'in', ['platform', 'system'])
    );
    const snapshot = await getDocs(q);
    
    const automationData = {
      ...data,
      category: 'platform',
      userId: 'system',
      updatedAt: Timestamp.now(),
    };

    if (!snapshot.empty) {
      const docId = snapshot.docs[0].id;
      await updateDoc(doc(db, 'automations', docId), automationData);
      return docId;
    } else {
      const docRef = await addDoc(collection(db, 'automations'), {
        ...automationData,
        createdAt: Timestamp.now(),
        stats: { sent: 0, opened: 0, clicked: 0 }
      });
      return docRef.id;
    }
  }
}

export const platformEmailService = new PlatformEmailService();
